import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const BusinessLookup = ({ onUse }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post("/business-lookup", { query: query.trim() });
      setResults(data.results);
      if (data.results.length === 0) toast.info("No results — try adding a city or more detail");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lookup failed");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="border border-border bg-[#0A0A0A] p-5 mb-6" data-testid="business-lookup">
      <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
        <Search className="w-3.5 h-3.5 text-[#007AFF]" /> Look up a business on the web
      </Label>
      <p className="text-[11px] text-muted-foreground mb-3">Don't have their contact details? Search the web and I'll suggest a name, website, phone numbers, manager/owner names, and any emails found.</p>
      <div className="flex gap-2">
        <Input value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          placeholder='e.g. "Rosario Produce Albuquerque"' data-testid="lookup-input"
          className="bg-background border-border rounded-sm focus-visible:ring-[#007AFF]" />
        <Button type="button" onClick={search} disabled={searching || !query.trim()} data-testid="lookup-btn"
          variant="outline" className="rounded-sm gap-2 border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 hover:text-[#007AFF] shrink-0">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </Button>
      </div>
      {results && results.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="lookup-results">
          {results.map((r, i) => (
            <div key={r.website || r.name || i} className="border border-border bg-background p-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{r.name}</p>
                <a href={r.website} target="_blank" rel="noreferrer" className="text-xs text-[#007AFF] hover:underline break-all">{r.website}</a>
                {r.emails.length > 0 && <p className="text-xs font-mono text-muted-foreground mt-1">{r.emails.join(" · ")}</p>}
                {r.phones?.length > 0 && (
                  <p className="text-xs font-mono text-muted-foreground mt-1 flex items-center gap-1.5" data-testid={`lookup-phones-${i}`}>
                    <Phone className="w-3 h-3 text-[#007AFF]" /> {r.phones.join(" · ")}
                  </p>
                )}
                {r.contacts?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5" data-testid={`lookup-contacts-${i}`}>
                    <User className="w-3 h-3 text-[#007AFF]" /> {r.contacts.join(" · ")}
                  </p>
                )}
              </div>
              <Button type="button" size="sm" variant="outline" data-testid={`lookup-use-${i}`}
                onClick={() => { onUse(r); toast.success("Details filled in — verify before sending"); }}
                className="rounded-sm h-7 text-xs border-border shrink-0">Use</Button>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground">Web results are leads, not verified contacts — double-check the email before sending.</p>
        </div>
      )}
    </div>
  );
};
